import Vue from 'vue'
import VueRouter from 'vue-router'
import { Toast } from 'vant'
import api from '@/services/api'
import { isMobile } from '@/utils/device'

Vue.use(VueRouter)

// 动态加载组件，根据设备类型选择 PC 端或移动端页面
function loadComponent(name) {
  const deviceType = isMobile() ? 'mobile' : 'pc'
  return () => import(`../views/${deviceType}/${name}.vue`)
}

const routes = [
  {
    path: '/',
    redirect: '/home'
  },
  {
    path: '/home',
    name: 'Home',
    component: loadComponent('Home'),
    meta: { keepAlive: true }
  },
  {
    path: '/map',
    name: 'Map',
    component: loadComponent('Map'),
    meta: { keepAlive: true }
  },
  {
    path: '/help',
    name: 'Help',
    component: loadComponent('Help'),
    meta: { keepAlive: true }
  },
  {
    path: '/tree',
    name: 'Tree',
    component: loadComponent('Tree'),
    meta: { keepAlive: true }
  },
  {
    path: '/person/create',
    name: 'PersonCreate',
    component: loadComponent('PersonCreate')
  },
  {
    path: '/person/:id',
    name: 'PersonDetail',
    component: loadComponent('PersonDetail'),
    meta: { keepAlive: true }
  },
  {
    path: '/person/:id/edit',
    name: 'PersonEdit',
    component: loadComponent('PersonCreate'),
    props: route => ({ editId: route.params.id })
  },
  {
    path: '/records',
    name: 'Records',
    component: loadComponent('Records'),
    meta: { keepAlive: true }
  },
  {
    path: '/record/create',
    name: 'RecordCreate',
    component: loadComponent('RecordCreate')
  },
  {
    path: '/record/:id/edit',
    name: 'RecordEdit',
    component: loadComponent('RecordCreate'),
    props: route => ({ editId: route.params.id })
  },
  {
    path: '/record/:id',
    name: 'RecordDetail',
    component: loadComponent('RecordDetail')
  },
  {
    path: '/share',
    name: 'Share',
    component: loadComponent('Share'),
    meta: { keepAlive: true }
  },
  {
    path: '/share/create',
    name: 'ShareCreate',
    component: loadComponent('ShareCreate')
  },
  {
    path: '/share-detail/:shareToken',
    name: 'ShareDetail',
    component: loadComponent('ShareDetail'),
    meta: { keepAlive: true, pcKeepAlive: true }
  },
  {
    path: '/public-shares',
    name: 'PublicShares',
    component: loadComponent('PublicShares'),
    meta: { keepAlive: true }
  },
  {
    // 公开祠堂列表：未登录用户从首页「先逛逛祠堂 / 查看更多」进入
    path: '/public-halls',
    name: 'PublicHalls',
    component: loadComponent('PublicHalls'),
    meta: { keepAlive: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: loadComponent('Profile'),
    meta: { keepAlive: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: loadComponent('Login')
  },
  {
    path: '/register',
    name: 'Register',
    component: loadComponent('Register')
  },
  {
    // 忘记密码/找回账号（用户名）页面
    path: '/forget-password',
    name: 'ForgetPassword',
    component: loadComponent('ForgetPassword')
  },
  {
    // 微信扫码登录授权回调页面，PC/移动端共用
    path: '/login/wechat-callback',
    name: 'WechatCallback',
    component: () => import('../views/WechatCallback.vue')
  },
  {
    path: '/friends',
    name: 'Friends',
    component: loadComponent('Friends'),
    meta: { keepAlive: true }
  },
  {
    path: '/chat/:friendId',
    name: 'Chat',
    component: loadComponent('Chat')
  },
  {
    path: '/profile/:userId',
    name: 'UserProfile',
    component: loadComponent('Profile')
  },
  {
    path: '/milestone',
    name: 'MilestoneTimeline',
    component: loadComponent('MilestoneTimeline'),
    meta: { keepAlive: true }
  },
  {
    path: '/milestone/create',
    name: 'MilestoneCreate',
    component: loadComponent('MilestoneCreate')
  },
  {
    path: '/milestone/:id',
    name: 'MilestoneDetail',
    component: loadComponent('MilestoneDetail')
  },
  {
    path: '/ancestral-hall',
    name: 'AncestralHallList',
    component: loadComponent('AncestralHallList'),
    meta: { keepAlive: true }
  },
  {
    path: '/ancestral-hall/create',
    name: 'AncestralHallCreate',
    component: loadComponent('AncestralHallCreate')
  },
  {
    path: '/ancestral-hall/:id/public',
    name: 'AncestralHallPublicDetail',
    component: loadComponent('AncestralHallPublicDetail')
  },
  {
    path: '/ancestral-hall/:id',
    name: 'AncestralHallDetail',
    component: loadComponent('AncestralHallDetail'),
    meta: { keepAlive: true }
  },
  {
    path: '/ancestral-hall/:id/edit',
    name: 'AncestralHallEdit',
    component: loadComponent('AncestralHallCreate')
  },
  // 家族列表（需登录）
  {
    path: '/family',
    name: 'FamilyList',
    component: loadComponent('FamilyList'),
    meta: { keepAlive: true }
  },
  // 创建家族必须定义在 /family/:id 之前，否则会被动态路由匹配吃掉
  {
    path: '/family/create',
    name: 'FamilyCreate',
    component: loadComponent('FamilyCreate')
  },
  {
    path: '/family/:id',
    name: 'FamilyDetail',
    component: loadComponent('FamilyDetail'),
    meta: { keepAlive: true }
  },
  // 编辑家族复用创建页，通过 editId 传入家族 id
  {
    path: '/family/:id/edit',
    name: 'FamilyEdit',
    component: loadComponent('FamilyCreate'),
    props: route => ({ editId: route.params.id })
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

// 重写push方法，解决重复导航错误
const originalPush = router.push
router.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated' && !err.message.includes('Navigation cancelled')) {
      throw err
    }
  })
}

// 重写replace方法，解决重复导航错误
const originalReplace = router.replace
router.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated' && !err.message.includes('Navigation cancelled')) {
      throw err
    }
  })
}

// 路由守卫
router.beforeEach(async (to, from, next) => {
  // 检查是否需要登录（公开分享页面、祠堂详情页、公开祠堂详情页不需要登录）
  const publicPaths = ['/login', '/register', '/forget-password', '/login/wechat-callback', '/public-shares', '/public-halls', '/home', '/map', '/help', '/ancestral-hall']
  // 祠堂详情页（/ancestral-hall/:id）允许未登录访问，但编辑页和创建页需要登录
  const isAncestralHallDetail = to.path.match(/^\/ancestral-hall\/\d+$/) && !to.path.endsWith('/edit')
  // 公开祠堂详情页（/ancestral-hall/:id/public）允许未登录访问
  const isPublicHallDetail = to.path.match(/^\/ancestral-hall\/\d+\/public$/)
  // 记录详情页（/record/:id）允许未登录访问，后端仅对“关联人物公开”的记录返回数据，编辑页仍要求登录
  const isRecordDetail = to.path.match(/^\/record\/\d+$/)
  const requiresAuth = !publicPaths.includes(to.path) && !to.path.startsWith('/share-detail/') && !isAncestralHallDetail && !isPublicHallDetail && !isRecordDetail
  const isLoggedIn = localStorage.getItem('user')

  if (requiresAuth && !isLoggedIn) {
    // 如果是从分享详情页面跳转到里程碑或记录详情，允许跳转
    if (from.name === 'ShareDetail' && (to.path.startsWith('/record/') || to.path.startsWith('/milestone/'))) {
      next()
    } else {
      next('/login')
    }
    return
  }

  // 人物详情页：校验查看权限，没有权限时不跳转并提示
  if (to.name === 'PersonDetail' && to.params.id) {
    try {
      const response = await api.person.checkViewPermission(to.params.id)
      if (response.data && response.data.canView === false) {
        Toast({ message: '没有权限查看该人物', position: 'top', duration: 3000 })
        next(false)
        return
      }
    } catch (error) {
      // 校验接口异常时不拦截，避免影响正常访问
      console.error('Check view permission error:', error)
    }
  }

  next()
})

export default router
